import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { CircularProgress, Fade, Menu, MenuItem } from '@mui/material';
import { CaretDown, CaretUp, Plus, ArrowsClockwise } from '@phosphor-icons/react';
import { Button, Text } from 'components';
import { Search } from 'components/inputs/Search';
import { useDialog } from 'features/modalManager';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useSearchParams } from 'react-router-dom';
import { omit } from 'lodash';
import { useNotification } from 'features/notifications';
import { useStoreState, useStoreActions } from 'stores/global';
import type { View, ViewConfig } from 'types/views';
import {
  useViews,
  useCreateView,
  useUpdateView,
  useDeleteView,
  useFetchView,
} from '../../api/useViews';
import { SortableViewItem } from './SortableViewItem';
import { parse } from 'features/urlState/hooks/useUrlState';
import { useIsMounted } from 'hooks/useIsMounted';
import {
  applyViewCriteriaToParams,
  hasViewFilterChanges,
  buildViewFilterCriteria,
  isViewFilterCriteriaTooLarge,
} from './viewCriteriaUtils';
import { useColumnStateChanged } from './useColumnStateChanged';
import { colors } from 'styles/theme/colors';

type ViewDropdownProps = {
  viewConfig: ViewConfig;
  tableName: string;
  onViewChange?: (view: View | null) => void;
};

export const ViewDropdown = ({ viewConfig, tableName, onViewChange }: ViewDropdownProps) => {
  const { entityType, label } = viewConfig;
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editingViewId, setEditingViewId] = useState<number | null>(null);
  const [viewSearch, setViewSearch] = useState('');
  const [fullViewData, setFullViewData] = useState<View | null>(null);
  const editValueRef = useRef<string>('');
  const pendingViewSelectRef = useRef<boolean>(false);
  const previousViewIdsRef = useRef<Set<number>>(new Set());
  // Tracks the last viewId we fired `onViewChange` for, so the fetchFullView
  // effect can safely fire on initial page load (URL already has viewId) without
  // double-firing right after a user-initiated `handleViewSelect`.
  const lastFiredViewIdRef = useRef<number | null>(null);
  // Stabilises `onViewChange` for use inside effects/callbacks so unmemoised
  // parent callbacks don't retrigger `fetchView` on every parent render.
  const onViewChangeRef = useRef(onViewChange);
  useEffect(() => {
    onViewChangeRef.current = onViewChange;
  }, [onViewChange]);
  const open = Boolean(anchorEl);
  const isMounted = useIsMounted();
  const { _ } = useLingui();

  const { openDialog } = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const { notify } = useNotification();
  const { persistedState } = useStoreState(state => state.urlState);
  const { updatePersistedState } = useStoreActions(state => state.urlState);

  const [lastViewId, setLastViewId] = useLocalStorageState<number | undefined>(
    `${tableName}-lastViewId`
  );

  const selectedViewId = useMemo(() => {
    const viewIdParam = searchParams.get('viewId');
    if (viewIdParam) {
      const parsed = parse(viewIdParam);
      return typeof parsed === 'number' ? parsed : undefined;
    }
    return lastViewId;
  }, [searchParams, lastViewId]);

  const { data: viewsData, isLoading, isError } = useViews(entityType);
  const { mutate: createView } = useCreateView(entityType);
  const { mutate: updateView } = useUpdateView(entityType);
  const { mutate: deleteView } = useDeleteView(entityType);
  const { fetchView } = useFetchView(entityType);

  const serverViews = viewsData?.views || [];

  const [optimisticViews, setOptimisticViews] = useState<View[] | null>(null);
  const prevServerViewsRef = useRef<View[]>(serverViews);

  useEffect(() => {
    const hasServerDataChanged =
      JSON.stringify(serverViews.map(v => v.id)) !==
      JSON.stringify(prevServerViewsRef.current.map(v => v.id));

    if (hasServerDataChanged) {
      setOptimisticViews(null);
      prevServerViewsRef.current = serverViews;
    }
  }, [serverViews]);

  const views = optimisticViews || serverViews;

  const filteredViews = views.filter(v =>
    v.viewName.toLowerCase().includes(viewSearch.toLowerCase())
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setViewSearch('');
  }, []);

  const handleViewSelect = useCallback(
    async (viewId: number, options?: { keepOpen?: boolean }) => {
      try {
        const viewData = await fetchView(viewId);

        if (viewData && isMounted()) {
          setFullViewData(viewData);
          setLastViewId(viewId);

          const newParams = new URLSearchParams();
          newParams.set('viewId', `n|${viewId}`);

          if (viewData.filterCriteria) {
            applyViewCriteriaToParams({ ...viewData.filterCriteria }, tableName, newParams);
          }

          setSearchParams(newParams, { replace: true });
          onViewChange?.(viewData);
        }
      } catch (err) {
        notify.error({
          message: t`Failed to load view. Please try again.`,
          id: 'view-load-error',
        });
      } finally {
        if (!options?.keepOpen) {
          handleClose();
        }
      }
    },
    [fetchView, setSearchParams, onViewChange, notify, handleClose, tableName, setLastViewId]
  );

  useEffect(() => {
    // Identify a just-created view by id-set diff rather than by array
    // position: the server may return views in any order (e.g. sorted by
    // viewName), so `views[views.length - 1]` is not reliably the newest.
    if (pendingViewSelectRef.current) {
      const newView = views.find(v => !previousViewIdsRef.current.has(v.id));
      if (newView?.id) {
        pendingViewSelectRef.current = false;
        previousViewIdsRef.current = new Set(views.map(v => v.id));
        editValueRef.current = newView.viewName;
        setEditingViewId(newView.id);
        handleViewSelect(newView.id, { keepOpen: true });
        return;
      }
    }
    previousViewIdsRef.current = new Set(views.map(v => v.id));
  }, [views, handleViewSelect]);

  const selectedView = views.find(v => v.id === selectedViewId) ?? null;

  useEffect(() => {
    // view was restored from localStorage (no viewId in URL) at the time
    const restoredFromStorage = !searchParams.has('viewId');

    const fetchFullView = async () => {
      if (selectedViewId && selectedViewId !== 0) {
        try {
          const data = await fetchView(selectedViewId);
          setFullViewData(data);

          // when restoring from localStorage update the url and table state
          if (restoredFromStorage) {
            const newParams = new URLSearchParams();
            newParams.set('viewId', `n|${selectedViewId}`);
            if (data.filterCriteria) {
              applyViewCriteriaToParams({ ...data.filterCriteria }, tableName, newParams);
            }
            setSearchParams(newParams, { replace: true });
          }

          // Fire onViewChange for the initial-load case (URL already has
          // viewId on mount) without double-firing after user-initiated
          // selects, which set the ref before this effect runs.
          if (lastFiredViewIdRef.current !== data.id) {
            lastFiredViewIdRef.current = data.id;
            onViewChangeRef.current?.(data);
          }
        } catch {
          setFullViewData(null);
          setLastViewId(undefined);
        }
      } else {
        setFullViewData(null);
      }
    };

    fetchFullView();
  }, [selectedViewId, fetchView, setLastViewId, tableName, setSearchParams]);

  const currentFilters = useMemo(() => {
    const urlState: Record<string, unknown> = {};
    searchParams.forEach((value, key) => {
      urlState[key] = parse(value);
    });
    return omit(urlState, ['taskId', 'viewId', 'page', 'cursor']);
  }, [searchParams]);

  const savedTs =
    typeof fullViewData?.filterCriteria?._ts === 'string' ? fullViewData.filterCriteria._ts : '';

  const columnStateChanged = useColumnStateChanged(tableName, savedTs);

  const hasFilterChanges = useMemo(() => {
    if (!selectedViewId || !fullViewData) return false;
    if (columnStateChanged) return true;
    return hasViewFilterChanges(currentFilters, { ...fullViewData.filterCriteria });
  }, [fullViewData, selectedViewId, currentFilters, tableName, columnStateChanged]);

  const defaultFilters: Record<string, string> = persistedState[`${tableName}-urlDefaults`] ?? {};

  const handleDeselectView = useCallback(() => {
    const next = new URLSearchParams();
    Object.entries(defaultFilters).forEach(([k, v]) => {
      if (v != null) next.set(k, v);
    });
    setSearchParams(next, { replace: true });
    lastFiredViewIdRef.current = null;
    onViewChangeRef.current?.(null);
    setLastViewId(undefined);
    updatePersistedState({ [`${tableName}-urlState`]: { ...defaultFilters } });
    handleClose();
  }, [
    defaultFilters,
    setSearchParams,
    handleClose,
    setLastViewId,
    updatePersistedState,
    tableName,
  ]);

  const handleAddView = () => {
    const urlState: Record<string, unknown> = {};
    searchParams.forEach((value, key) => {
      urlState[key] = parse(value);
    });
    const baseFilters = omit(urlState, ['taskId', 'viewId', 'page', 'cursor']);
    const filterCriteria = buildViewFilterCriteria(baseFilters, tableName);

    if (isViewFilterCriteriaTooLarge(filterCriteria)) {
      notify.error({
        message: t`View is too large to save. Try reducing the number of active filters.`,
        id: 'view-too-large-error',
      });
      return;
    }

    pendingViewSelectRef.current = true;
    createView(
      { data: { filterCriteria } },
      {
        onSuccess: () => {
          notify.success({
            message: t`New view saved!`,
            id: `view-create-success-${entityType}`,
          });
        },
      }
    );
  };

  const handleEditView = (view: View) => {
    if (editingViewId === view.id) {
      setEditingViewId(null);
    } else {
      // Initialize the ref with the current view name when starting to edit
      editValueRef.current = view.viewName;
      setEditingViewId(view.id);
    }
  };

  const handleEditValueChange = (newValue: string) => {
    editValueRef.current = newValue;
  };

  const handleEditValidationError = () => {
    notify.error({
      message: t`View name cannot be empty`,
      id: 'view-name-empty-error',
    });
  };

  const handleSaveEdit = useCallback(
    (viewId: number, newName: string) => {
      if (!newName.trim()) {
        handleEditValidationError();
        return;
      }

      updateView(
        {
          data: { viewName: newName },
          urlParams: [entityType, viewId.toString()],
        },
        {
          onSuccess: () => {
            setEditingViewId(null);
            notify.success({
              message: t`View renamed`,
              id: `view-rename-success-${entityType}`,
            });
          },
          onError: () => {
            notify.error({
              message: t`Failed to rename view. Please try again.`,
              id: `view-rename-error-${entityType}`,
            });
          },
        }
      );
    },
    [updateView, entityType, notify]
  );

  useEffect(() => {
    if (editingViewId === null) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isClickInsideInput = target.closest('[data-editing-view]');
      if (!isClickInsideInput) {
        // Delay closing to allow onBlur handler to complete first
        setTimeout(() => {
          setEditingViewId(null);
        }, 0);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingViewId]);

  const handleSaveViewFilters = () => {
    if (!fullViewData || !selectedViewId) return;

    const filterCriteria = buildViewFilterCriteria(currentFilters, tableName);

    if (isViewFilterCriteriaTooLarge(filterCriteria)) {
      notify.error({
        message: t`View is too large to save. Try reducing the number of active filters.`,
        id: 'view-too-large-error',
      });
      return;
    }

    updateView(
      {
        data: { filterCriteria },
        urlParams: [entityType, selectedViewId.toString()],
      },
      {
        onSuccess: () => {
          setFullViewData({
            ...fullViewData,
            filterCriteria,
          });

          notify.success({
            message: t`Changes saved to current view`,
            id: 'view-filters-update-success',
            actions: [{ id: 'dismiss', label: t`Dismiss` }],
          });
        },
      }
    );
  };

  const handleDeleteView = async (viewId: number) => {
    const result = await openDialog({
      id: 'deleteView',
      title: t`You are in the process of deleting 1 saved view`,
      description: t`This action will permanently remove the view from your saved views.`,
      actions: [
        {
          label: t`Cancel`,
          variant: 'secondary',
          id: 'close',
          size: 'lg',
          sx: {
            '&&:hover': {
              backgroundColor: theme.palette.bg.brandPrimary,
            },
          },
        },
        {
          label: t`Delete view`,
          variant: 'primary',
          id: 'delete',
          color: 'error',
          size: 'lg',
          sx: {
            '&&': {
              backgroundColor: theme.palette.bg.errorSolid,
              borderColor: theme.palette.bg.errorSolid,
              color: theme.palette.text.primaryOnBrand,
            },
            '&&:hover': {
              backgroundColor: colors.error[600],
              borderColor: colors.error[600],
            },
            '&&:active': {
              boxShadow: `0px 0px 0px 4px ${colors.error[600]}29`,
            },
          },
        },
      ],
      hideDontShowAgain: true,
      modalSx: {
        window: { minWidth: '560px', maxWidth: '560px' },
        windowBody: { padding: '4px 24px 24px 24px', flex: '0 0 auto' },
        windowActions: {
          paddingTop: '24px',
          paddingRight: '24px',
          paddingBottom: '24px',
          paddingLeft: '24px',
          gap: '12px',
        },
      },
    });

    if (result === 'delete') {
      const wasSelected = viewId === selectedViewId;
      // Snapshot selection state so we can restore it if the delete fails;
      // otherwise the user silently loses their active view while it still
      // exists on the server.
      const prevSearchParams = wasSelected ? new URLSearchParams(searchParams) : null;
      const prevLastViewId = wasSelected ? lastViewId : undefined;

      if (wasSelected) {
        handleDeselectView();
      }

      deleteView(
        {
          ids: [{ id: viewId }],
        },
        {
          onSuccess: () => {
            notify.success({
              message: t`View deleted`,
              id: `view-delete-success-${entityType}`,
            });
          },
          onError: () => {
            if (wasSelected && prevSearchParams) {
              setSearchParams(prevSearchParams, { replace: true });
              setLastViewId(prevLastViewId);
            }
            notify.error({
              message: t`Failed to delete view. Please try again.`,
              id: `view-delete-error-${entityType}`,
            });
          },
        }
      );
    }
  };

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <Button
        sx={{
          minWidth: '180px',
          borderRadius: '8px',
          border: 'none',
          background: open ? theme.palette.bg.brandPrimary : 'transparent',
        }}
        disabled
      >
        <CircularProgress size={16} />
      </Button>
    );
  }

  return (
    <>
      <ViewContainer>
        <Button
          sx={{
            width: '200px',
            border: 'none',
            background: open ? theme.palette.bg.brandPrimary : 'transparent',
            justifyContent: 'space-between',
            padding: '10px 16px',
            '&:hover': {
              background: theme.palette.bg.brandPrimary,
              '& span': {
                color: theme.palette.text.brandSecondary,
              },
              '& svg': {
                fill: theme.palette.text.brandSecondary,
              },
            },
            '& .MuiButton-endIcon': {
              marginLeft: 'auto',
              flexShrink: 0,
            },
            '& .MuiButton-startIcon': {
              flexShrink: 0,
            },
            '& span': {
              color: open ? theme.palette.text.brandSecondary : theme.palette.text.primary,
            },
            '& svg': open ? { fill: theme.palette.text.brandSecondary } : {},
          }}
          id="view-dropdown-button"
          aria-controls={open ? 'view-dropdown-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          endIcon={
            open ? (
              <CaretUp size={16} color={theme.palette.text.brandSecondary} />
            ) : (
              <CaretDown size={16} color={theme.palette.text.secondary} />
            )
          }
        >
          <Text variant="labelMd" noWrap>
            {selectedView?.viewName || t`Show all ${_(label)}`}
          </Text>
        </Button>
      </ViewContainer>
      <Menu
        id="view-dropdown-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slots={{ transition: Fade }}
        slotProps={{
          paper: {
            sx: {
              width: '240px',
              marginTop: theme.s.xs,
              borderRadius: '8px',
              p: 0,
              maxHeight: '360px',
              display: 'flex',
              flexDirection: 'column',
            },
          },
          list: {
            sx: {
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            },
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <SearchWrapper onKeyDown={e => e.stopPropagation()}>
          <Search
            value={viewSearch}
            onChange={setViewSearch}
            placeholder={t`Search views`}
            searchOpen
            enableKeyboardShortcuts={false}
            fullWidth
          />
        </SearchWrapper>
        <ScrollableContent>
          {hasFilterChanges && selectedView ? (
            <AddViewItem onClick={handleSaveViewFilters}>
              <ArrowsClockwise size={16} color={theme.palette.fg.primary} />
              <span>
                <Trans>Save to current view</Trans>
              </span>
            </AddViewItem>
          ) : null}
          {views.length < 100 ? (
            <AddViewItem onClick={handleAddView}>
              <Plus size={16} color={theme.palette.fg.primary} />
              <span>
                <Trans>Save as new view</Trans>
              </span>
            </AddViewItem>
          ) : null}
          <Divider />
          <ShowAllItem isSelected={!selectedViewId} onClick={handleDeselectView}>
            <Text variant="labelSm" noWrap>
              <Trans>Show all {_(label)}</Trans>
            </Text>
          </ShowAllItem>
          <SectionLabel>
            <Text variant="labelSm" weight="semiBold" color="placeholder">
              <Trans>Custom views</Trans>
            </Text>
          </SectionLabel>
          {filteredViews.length > 0
            ? filteredViews
                .sort((a, b) => a.viewName.localeCompare(b.viewName))
                .map(view => (
                  <SortableViewItem
                    key={view.id}
                    view={view}
                    isSelected={view.id === selectedViewId}
                    isEditing={editingViewId === view.id}
                    onSelect={handleViewSelect}
                    onEdit={handleEditView}
                    onDelete={handleDeleteView}
                    onSaveEdit={handleSaveEdit}
                    onEditValueChange={handleEditValueChange}
                    onEditValidationError={handleEditValidationError}
                  />
                ))
            : null}
          {views.length > 0 && filteredViews.length === 0 ? (
            <EmptyState>
              <Text variant="bodySm" color="placeholder">
                <Trans>No views match your search</Trans>
              </Text>
            </EmptyState>
          ) : null}
          {views.length === 0 ? (
            <EmptyState>
              <Text variant="bodySm" color="placeholder">
                <Trans>No custom views saved</Trans>
              </Text>
            </EmptyState>
          ) : null}
        </ScrollableContent>
      </Menu>
    </>
  );
};

const ViewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xs};
`;

const ScrollableContent = styled.div`
  padding: 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${p => p.theme.palette.border.primarySubtle};
  margin: 0;
`;

const AddViewItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xs};
  margin: ${p => p.theme.s.xxs};
  padding: ${p => p.theme.s.xs};
  height: 44px;
  border-radius: ${p => p.theme.s.xxs};
  color: ${p => p.theme.palette.text.primary};
  font-size: ${p => p.theme.typography.labelSm.fontSize};
  font-weight: ${p => p.theme.typography.weight.semiBold};

  &:hover {
    background-color: ${p => p.theme.palette.utility.grey[50]};
    color: ${p => p.theme.palette.text.successNeutral};
    svg {
      fill: ${p => p.theme.palette.text.successNeutral};
    }
  }
`;

const SearchWrapper = styled.li`
  /* ovrrides Search component styles to fit into the menu */
  > div {
    height: 48px;
  }
  && .MuiInputBase-root {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  margin: ${p => p.theme.s.xxs};
  padding: ${p => p.theme.s.xs};
  height: 44px;
  gap: ${p => p.theme.s.xs};
  font-style: italic;
`;

const SectionLabel = styled.li`
  margin: ${p => p.theme.s.xxs};
  padding: 0 ${p => p.theme.s.xs};
  list-style: none;
`;

const ShowAllItem = styled(MenuItem)<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  margin: ${p => p.theme.s.xxs};
  padding: ${p => p.theme.s.xs};
  height: 44px;
  border-radius: ${p => p.theme.s.xxs};
  cursor: pointer;
  background-color: ${p => (p.isSelected ? p.theme.palette.bg.brandSubtle : 'transparent')};
  text-shadow: ${p => (p.isSelected ? '0 0 0.5px currentColor' : 'none')};

  &:hover {
    background-color: ${p => p.theme.palette.utility.grey[50]};
  }
`;
